import CommonModal from 'components/common/CommonModal';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import {changeTongPlaceOperationStates} from 'function/api';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';
import {toDateOrUndefined} from 'utils/toDate';

const INPUT_DATE_FORMAT = 'YYYY-MM-DD';

/** API 응답의 날짜(ISO 문자열)를 date input 이 받는 YYYY-MM-DD 로 바꾼다. */
const toInputDate = (value: unknown): string => {
  const date = toDateOrUndefined(value);
  return moment(date ?? new Date()).format(INPUT_DATE_FORMAT);
};

/**
 * YYYY-MM-DD 를 현장 자정 기준 ISO 문자열로 바꾼다.
 * moment(v, 'YYYY-MM-DD') 는 로컬(KST) 자정으로 해석하므로
 * 기존에 저장된 값(KST 자정 = UTC 전일 15:00)과 같은 형식이 된다.
 */
const toApiDate = (value: string): string =>
  moment(value, INPUT_DATE_FORMAT).toDate().toISOString();

type Props = {
  selectItem: any;
  closeModal: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

const ChangeEndDateStateModal = ({
  selectItem,
  closeModal,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);

  /**
   * 날짜 입력은 BackOffice 의 다른 화면(변경 이력, 운영 현황 분석)과 같은
   * 네이티브 date input 을 쓴다.
   *
   * 이전에는 이 모달만 react-calendar 기반 LabelDate 를 썼는데, 달력이
   * `position: absolute` 로 입력칸 아래에 펼쳐지는 반면 CommonModal 컨테이너에
   * `overflow: hidden` 이 걸려 있어 달력이 모달 밖으로 잘려 나갔다.
   * 그래서 날짜 칸을 아예 클릭할 수 없었다(z-index 로는 해결되지 않는다).
   */
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [completeModal, setCompleteModal] = useState(false);

  useEffect(() => {
    return () => {
      setCompleteModal(false);
    };
  }, []);

  useEffect(() => {
    if (selectItem) {
      setStartDate(toInputDate(selectItem?.constructionStartDate));
      setEndDate(toInputDate(selectItem?.constructionEndDate));
    }
  }, [selectItem]);

  return (
    <>
      <CommonModal closeModal={closeModal}>
        <BusinessModalContainer>
          <BusinessModalHeader>
            <DefaultText
              text="공사기간"
              textStyle={{
                fontSize: minmax(40),
              }}
            />
            <DefaultImageView
              source="images/common/close.png"
              onClick={closeModal}
              style={{
                width: minmax(30),
                height: minmax(30),
                margin: minmax(15),
                cursor: 'pointer',
              }}
            />
          </BusinessModalHeader>

          <FieldArea>
            <Field>
              <FieldLabel htmlFor="constructionStartDate">
                공사 시작일
              </FieldLabel>
              <DateInput
                id="constructionStartDate"
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setStartDate(event.target.value)
                }
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="constructionEndDate">공사 종료일</FieldLabel>
              <DateInput
                id="constructionEndDate"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEndDate(event.target.value)
                }
              />
            </Field>

            <DefaultButton
              text="공사기간 변경"
              actived={1}
              buttonStyle={{
                width: '100%',
                backgroundColor: colors.primary2,
              }}
              textStyle={{fontSize: minmax(25)}}
              onClick={async () => {
                if (!startDate || !endDate) return;
                // 하루짜리 공사(시작일 = 종료일)를 허용한다.
                if (moment(startDate).isAfter(moment(endDate))) {
                  setErrorModal({
                    onOff: true,
                    title: '공사기간 오류',
                    detail: '공사 시작일은 종료일보다 늦을 수 없습니다.',
                  });
                  return;
                }
                const result = await changeTongPlaceOperationStates({
                  userInfo,
                  setIsLoading,
                  setErrorModal,
                  tongPlaceUUID: selectItem.uuid,
                  constructionStartDate: toApiDate(startDate),
                  constructionEndDate: toApiDate(endDate),
                });
                if (result) setCompleteModal(true);
              }}
            />
          </FieldArea>
        </BusinessModalContainer>
      </CommonModal>
      {completeModal && (
        <CommonModal
          title="공사기간 변경 완료!"
          detail="공사기간이 변경되었습니다."
          firstButtonText="confirm"
          firstButtonClick={() => {
            closeModal();
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default ChangeEndDateStateModal;

const BusinessModalContainer = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${minmax(20)};
`;

const BusinessModalHeader = styled.div`
  width: 100%;
  margin-bottom: ${minmax(10)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const FieldArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: ${minmax(20)};
`;

const Field = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FieldLabel = styled.label`
  width: 30%;
  font-size: ${minmax(20)};
  font-weight: 400;
  color: ${colors.black};
`;

const DateInput = styled.input`
  width: 50%;
  height: ${minmax(50)};
  padding: 0 ${minmax(12)};
  border: 1px solid ${colors.borderGray};
  border-radius: ${minmax(6)};
  color: ${colors.black};
  font-size: ${minmax(20)};
`;
